<template>
  <form @submit.prevent="save">
    <transition name="overlay-fade"></transition>
    <div class="dialog-header">
      <h1 v-if="isNew">Nieuwe collectie</h1>
      <h1 v-else>Collectie wijzigen</h1>
    </div>
    <div class="dialog-content">
      <template v-if="categories.length === 0">
        <p>
          Voordat u een collectie aan kunt maken dient u eerst een nieuwe
          categorie aan te maken.
        </p>
        <div class="button-holder">
          <button type="button" @click="$emit('close')" class="pill-button">
            Sluiten
          </button>
        </div>
      </template>
      <template v-else>
        <div class="field-wrapper">
          <div class="field-100 label-fix">
            <label for="name">Naam</label>
            <div>
              <input
                id="name"
                type="text"
                v-model="collection.name"
                name="name"
                ref="name"
                :class="{ invalid: $v.collection.name.$invalid }"
                placeholder="Geef de collectie een herkenbare naam"
                @input="$v.collection.$touch()"
              />
            </div>
          </div>
        </div>

        <div>
          <div class="field-100 label-fix">
            <label class="">Categorie</label>
            <div class="category-list">
              <span
                v-for="category in categories"
                :key="category.id"
                @click="setCategory(category)"
                class="category-item"
              >
                <select-box
                  @action="toggleCategory(category.id)"
                  :selected="
                    collection.categoryIds &&
                    collection.categoryIds.includes(category.id)
                  "
                  :color="theme.subColor"
                ></select-box>

                <label @click="toggleCategory(category.id)">{{
                  category.name
                }}</label>
              </span>
            </div>
          </div>
        </div>

        <div class="field-wrapper">
          <div class="field-100 label-fix">
            <label for="name">Ondertitel</label>
            <div>
              <input
                id="subtitle"
                type="text"
                v-model="collection.subtitle"
                name="subtitle"
                ref="subtitle"
                placeholder="Ondertitel voor op de collectiecard"
                @input="$v.collection.$touch()"
              />
            </div>
          </div>
        </div>

        <div class="field-wrapper">
          <div class="field-100 label-fix">
            <label for="description">Beschrijving</label>
            <div>
              <textarea
                id="description"
                type="text"
                v-model="collection.description"
                name="description"
                :class="{ invalid: $v.collection.description.$invalid }"
                placeholder="Voeg een heldere beschrijving van de collectie toe"
                @input="$v.collection.$touch()"
              ></textarea>
            </div>
          </div>
        </div>

        <div class="field-wrapper preview">
          <div class="field-100 label-fix">
            <label>Preview</label>
            <span v-if="collection.previewImage" class="preview-remove-button">
              <close-button @click="removePreview" color="white"></close-button>
            </span>
            <section class="preview-container">
              <img
                v-if="collection.previewImage"
                class="preview-image"
                :src="`/media/print-store/${rootStore.user.printStore.id}/preview/${collection.previewImage}/300`"
              />
              <print-store-file-upload
                v-show="showPreviewUpload"
                caption="afbeelding toevoegen"
                @uploadComplete="onUploadComplete($event)"
                @uploadStart="onUploadStart"
                :is-preview="true"
                class="margin-correction"
              ></print-store-file-upload>
              <bouncing-spinner v-if="uploadingPreview"></bouncing-spinner>
            </section>
          </div>
        </div>

        <div class="field-wrapper">
          <div class="field-100 label-fix">
            <label for="description">Verbergen</label>
            <select-box
              @action="setHidden()"
              :selected="!collection.active"
              :color="theme.subColor"
            ></select-box>

            <span class="placeholder"
              >Zichtbaarheid van de collectie voor anderen</span
            >
          </div>
        </div>

        <div class="field-wrapper">
          <div class="field-100 label-fix">
            <label for="description">Uitgelicht</label>
            <select-box
              @action="setDisplayOnDashboard()"
              :selected="collection.displayOnDashboard"
              :color="theme.subColor"
            ></select-box>
            <span class="placeholder"
              >De collectie tonen op de startpagina</span
            >

            <!-- <span v-if="collection.displayOnDashboard"
              >Collectie wordt getoond bij aanbevolen</span
            >
            <span v-else>Collectie wordt niet getoond bij aanbevolen</span> -->
          </div>
        </div>

        <div class="button-holder">
          <button
            class="pill-button secondary cancel"
            type="button"
            @click="$emit('close')"
          >
            annuleren
          </button>
          <button
            id="confirm-button"
            class="pill-button"
            type="submit"
            v-show="!saving"
            :disabled="saveButtonDisabled()"
          >
            <span v-if="isNew">Creëren</span>
            <span v-else>Wijzigen</span>
          </button>
        </div>
      </template>
    </div>
  </form>
</template>
<script>
import {
  addCollection,
  updateCollection
} from '@/features/printstore/PrintStoreApi'
import { uuidv4 } from '@/api/uuid'
import { EventBus, Events } from '@/eventbus/EventBus'
import { minLength, required } from '@vuelidate/validators'
import { logger } from '@/logger'
import PrintStoreFileUpload from '@/features/printstore/PrintStoreFileUpload'
import isEmpty from 'lodash-es/isEmpty'
import { mapStores } from 'pinia'
import { usePrintStoreStore } from '@/features/printstore/PrintStore.store'

export default {
  props: {
    value: {
      type: Object,
      default: () => {
        return {
          name: '',
          category: '',
          description: '',
          active: true,
          displayOnDashboard: false,
          categoryIds: []
        }
      }
    },
    callback: Function
  },
  data() {
    return {
      saving: false,
      isNew: !this.value.id,
      collection: {},
      showPreviewUpload: false,
      uploadingPreview: false
    }
  },
  components: {
    PrintStoreFileUpload
  },
  computed: {
    categories() {
      return this.printStoreStore.categories
    },
    ...mapStores(usePrintStoreStore)
  },
  mounted() {
    this.collection = { ...this.value }
    if (!this.collection.displayOnDashboard) {
      this.$set(this.collection, 'displayOnDashboard', false)
    }

    const categoryId =
      this.collection.categoryId || this.$route.params.categoryId

    if (categoryId) {
      const category = this.printStoreStore.getCategory(categoryId)
      this.collection.category = category.name
      this.collection.categoryId = category.id
      this.$forceUpdate()
    }

    if (this.$refs.name) {
      this.$refs.name.focus()
    }

    this.showPreviewUpload = isEmpty(this.collection.previewImage)
  },
  methods: {
    saveButtonDisabled() {
      return this.$v.collection.$invalid || !this.$v.collection.$anyDirty
    },
    setCategory(category) {
      this.collection.category = category.name
      this.collection.categoryId = category.id
      this.$v.collection.$touch()
      this.$forceUpdate()
    },
    setHidden() {
      this.collection.active = !this.collection.active
      this.$v.collection.$touch()
    },
    setDisplayOnDashboard() {
      this.collection.displayOnDashboard = !this.collection.displayOnDashboard
      this.$v.collection.$touch()
    },
    save() {
      if (this.isNew) {
        const categoryId = this.collection.categoryId
        const model = {
          ...this.collection,
          id: uuidv4()
        }
        delete model.category
        delete model.categoryId

        addCollection(model)
          .then(() => {
            EventBus.emit(Events.CATEGORY_CONTENT_UPDATED)
            this.$emit('close')
            this.$router.push({
              name: 'printStoreCollection',
              params: {
                categoryId: categoryId,
                collectionId: model.id
              }
            })
          })
          .catch((error) => logger.error(error))
      } else {
        const model = { ...this.collection }
        delete model.category
        delete model.categoryId

        updateCollection(this.collection).then(() => {
          EventBus.emit(Events.CATEGORY_CONTENT_UPDATED)

          this.callback(this.collection)
          this.$emit('close')
        })
      }
    },
    onUploadStart() {
      this.uploadingPreview = true
      this.showPreviewUpload = false
    },
    onUploadComplete(message) {
      this.collection.previewImage = message.uuid
      this.collection.coverImageId = message.fileId
      this.uploadingPreview = false
      this.$forceUpdate()
      this.$v.collection.$touch()
    },
    removePreview() {
      this.collection.previewImage = undefined
      this.collection.coverImageId = undefined
      this.showPreviewUpload = true
      this.$v.collection.$touch()
    },
    toggleCategory(id) {
      if (this.collection.categoryIds.includes(id)) {
        this.collection.categoryIds = this.collection.categoryIds.filter(
          (item) => item !== id
        )
      } else {
        this.collection.categoryIds.push(id)
      }
      this.$v.collection.$touch()
    }
  },
  validations: {
    collection: {
      name: {
        required
      },
      categoryIds: {
        required,
        minLength: minLength(1)
      },
      description: {
        required
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.chevron {
  color: var(--sub-color);
  padding-left: 8px;
  vertical-align: top;

  &.invalid {
    color: var(--error-color);
  }
}

.category {
  color: var(--sub-color);
  font-weight: 500;
}

.category-list {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  margin-bottom: 16px;
  .category-item {
    display: inline-flex;
    justify-content: flex-start;
    align-items: flex-end;
    width: 200px;
    gap: 6px;
    height: 24px;

    input {
      margin: 0;
    }

    label {
      width: unset;
    }
  }
}

.popup {
  display: inline-block;
  vertical-align: text-top;
}

label {
  width: 80px;
}

textarea {
  width: 100%;
  height: 120px;
  line-height: 18px;
}

.field-wrapper {
  margin-bottom: 20px;
}

.cancel {
  margin-right: 5px;
}

.preview {
  position: relative;

  label {
    margin-top: 3px;
  }

  .preview-container {
    float: left;
    position: relative;
    width: 141px;

    img {
      max-height: 141px;
      max-width: 100%;
    }
  }
}
</style>
